import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

/**
 * Reveal — premium fade-up on scroll wrapper.
 * Uses IntersectionObserver, respects prefers-reduced-motion, animates only once.
 *
 * Props:
 *  - delay: 1 | 2 | 3 | 4   (visual stagger via CSS class)
 *  - as:    HTML tag        (default 'div')
 *  - className, children, ...rest
 */
export default function Reveal({
    children,
    delay = 0,
    as: Tag = "div",
    className,
    ...rest
}) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const node = ref.current;
        if (!node) return;
        if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
            setVisible(true);
            return;
        }
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setVisible(true);
                        obs.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
        );
        obs.observe(node);
        return () => obs.disconnect();
    }, []);

    const delayClass = delay >= 1 && delay <= 4 ? `reveal-delay-${delay}` : "";

    return (
        <Tag
            ref={ref}
            className={cn("reveal", delayClass, visible && "is-visible", className)}
            {...rest}
        >
            {children}
        </Tag>
    );
}
